import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    motion,
    AnimatePresence,
    useScroll,
    useSpring,
    useTransform,
} from "motion/react";
import { HiArrowUp } from "react-icons/hi2";

const FixedButton = () => {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pct, setPct] = useState(0);
    useEffect(() => setMounted(true), []);
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, {
        stiffness: 180,
        damping: 28,
        mass: 0.4,
    });
    const pctValue = useTransform(progress, (v) => Math.round(v * 100));

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 320);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const unsub = pctValue.on("change", (v) => setPct(v));
        return () => unsub();
    }, [pctValue]);

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const R = 27;
    const C = 2 * Math.PI * R;

    // Particle positions for hover burst
    const particles = Array.from({ length: 8 }, (_, i) => ({
        angle: (i / 8) * Math.PI * 2,
        color: i % 2 === 0 ? "#7F00FF" : "#FCFA02",
    }));

    if (!mounted) return null;

    const content = (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key='scroll-top'
                    initial={{ opacity: 0, y: 30, scale: 0.5 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 30, scale: 0.5 }}
                    transition={{ type: "spring", stiffness: 280, damping: 22 }}
                    onHoverStart={() => setHovered(true)}
                    onHoverEnd={() => setHovered(false)}
                    className='client-fab fixed bottom-6 right-6 md:bottom-8 md:right-8 lg:right-auto lg:left-8 z-50 flex items-center gap-3'
                >
                    {/* Tooltip pill */}
                    <AnimatePresence>
                        {hovered && (
                            <motion.div
                                initial={{ opacity: 0, x: 14, scale: 0.85 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 14, scale: 0.85 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 360,
                                    damping: 24,
                                }}
                                className='hidden md:flex lg:hidden items-center gap-2 px-4 py-2 rounded-full pointer-events-none relative'
                                style={{
                                    background: "rgba(18,18,18,0.7)",
                                    backdropFilter: "blur(18px) saturate(180%)",
                                    WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    boxShadow:
                                        "0 10px 28px -10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
                                }}
                            >
                                <span className='text-white text-xs font-semibold tracking-wide'>
                                    Back to top
                                </span>
                                <span
                                    className='text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        color: "#fff",
                                    }}
                                >
                                    {pct}%
                                </span>
                                <span
                                    aria-hidden
                                    className='absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45'
                                    style={{
                                        background: "rgba(18,18,18,0.7)",
                                        borderRight: "1px solid rgba(255,255,255,0.12)",
                                        borderTop: "1px solid rgba(255,255,255,0.12)",
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Button container with idle float */}
                    <motion.div
                        animate={{ y: hovered ? 0 : [0, -4, 0] }}
                        transition={
                            hovered
                                ? { duration: 0.2 }
                                : { duration: 2.8, repeat: Infinity, ease: "easeInOut" }
                        }
                        className='relative'
                    >
                        {/* Particle burst on hover */}
                        <AnimatePresence>
                            {hovered &&
                                particles.map((p, i) => (
                                    <motion.span
                                        key={`p-${i}`}
                                        initial={{
                                            x: 0,
                                            y: 0,
                                            opacity: 0,
                                            scale: 0,
                                        }}
                                        animate={{
                                            x: Math.cos(p.angle) * 38,
                                            y: Math.sin(p.angle) * 38,
                                            opacity: [0, 1, 0],
                                            scale: [0, 1, 0.4],
                                        }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                            duration: 0.9,
                                            repeat: Infinity,
                                            delay: i * 0.08,
                                            ease: "easeOut",
                                        }}
                                        className='absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full pointer-events-none'
                                        style={{
                                            background: p.color,
                                            boxShadow: `0 0 8px ${p.color}`,
                                            translateX: "-50%",
                                            translateY: "-50%",
                                        }}
                                    />
                                ))}
                        </AnimatePresence>

                        {/* Pulsing halos */}
                        <motion.span
                            aria-hidden
                            animate={{
                                scale: hovered ? [1, 1.8, 1] : [1, 1.55, 1],
                                opacity: hovered ? [0.7, 0, 0.7] : [0.5, 0, 0.5],
                            }}
                            transition={{
                                duration: hovered ? 1.4 : 2.4,
                                repeat: Infinity,
                                ease: "easeOut",
                            }}
                            className='absolute inset-0 rounded-full pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(127,0,255,0.6), transparent 70%)",
                            }}
                        />
                        <motion.span
                            aria-hidden
                            animate={{
                                scale: hovered ? [1, 2, 1] : [1, 1.75, 1],
                                opacity: hovered ? [0.5, 0, 0.5] : [0.3, 0, 0.3],
                            }}
                            transition={{
                                duration: hovered ? 1.4 : 2.4,
                                repeat: Infinity,
                                ease: "easeOut",
                                delay: hovered ? 0.4 : 0.8,
                            }}
                            className='absolute inset-0 rounded-full pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(252,250,2,0.4), transparent 70%)",
                            }}
                        />

                        <motion.button
                            type='button'
                            onClick={handleClick}
                            aria-label='Back to top'
                            initial='rest'
                            animate={hovered ? "hover" : "rest"}
                            whileTap='tap'
                            variants={{
                                rest: {
                                    scale: 1,
                                    rotate: 0,
                                    boxShadow:
                                        "0 18px 40px -12px rgba(127,0,255,0.6), 0 8px 22px -8px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.22)",
                                },
                                hover: {
                                    scale: 1.15,
                                    rotate: -8,
                                    boxShadow:
                                        "0 28px 60px -10px rgba(127,0,255,0.85), 0 14px 32px -8px rgba(252,250,2,0.35), 0 8px 22px -8px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.32)",
                                    transition: {
                                        type: "spring",
                                        stiffness: 380,
                                        damping: 14,
                                    },
                                },
                                tap: { scale: 0.88, rotate: 0 },
                            }}
                            className='group relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center overflow-hidden'
                            style={{
                                background:
                                    "linear-gradient(145deg, rgba(127,0,255,0.18), rgba(18,18,18,0.7))",
                                backdropFilter: "blur(22px) saturate(180%)",
                                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.16)",
                            }}
                        >
                            {/* Animated gradient body fill */}
                            <motion.div
                                aria-hidden
                                variants={{
                                    rest: { opacity: 0, scale: 0.7 },
                                    hover: { opacity: 1, scale: 1.2 },
                                }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                className='absolute inset-0 pointer-events-none'
                                style={{
                                    background:
                                        "radial-gradient(circle at 30% 30%, rgba(127,0,255,0.55), rgba(255,138,255,0.3) 45%, transparent 80%)",
                                }}
                            />

                            {/* Conic rotating accent */}
                            <motion.div
                                aria-hidden
                                animate={{ rotate: hovered ? 360 : 0 }}
                                transition={{
                                    duration: 2.2,
                                    repeat: hovered ? Infinity : 0,
                                    ease: "linear",
                                }}
                                variants={{
                                    rest: { opacity: 0 },
                                    hover: { opacity: 0.7 },
                                }}
                                className='absolute inset-0 pointer-events-none'
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, transparent, rgba(127,0,255,0.6), transparent 30%, rgba(252,250,2,0.5), transparent 60%, rgba(255,138,255,0.6), transparent)",
                                    filter: "blur(5px)",
                                }}
                            />

                            {/* Sweeping shine */}
                            <motion.div
                                aria-hidden
                                variants={{
                                    rest: { x: "-120%", opacity: 0 },
                                    hover: { x: "120%", opacity: 1 },
                                }}
                                transition={{ duration: 0.75, ease: "easeInOut" }}
                                className='absolute inset-y-0 w-1/2 pointer-events-none'
                                style={{
                                    background:
                                        "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)",
                                    filter: "blur(2px)",
                                }}
                            />

                            {/* Glossy top highlight */}
                            <motion.div
                                aria-hidden
                                variants={{
                                    rest: { opacity: 0.5 },
                                    hover: { opacity: 0.85 },
                                }}
                                className='absolute inset-x-2 top-1 h-1/3 rounded-full pointer-events-none'
                                style={{
                                    background:
                                        "linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)",
                                    filter: "blur(4px)",
                                }}
                            />

                            {/* Circular progress ring */}
                            <svg
                                className='absolute inset-0 w-full h-full -rotate-90 pointer-events-none'
                                viewBox='0 0 60 60'
                            >
                                <circle
                                    cx='30'
                                    cy='30'
                                    r={R}
                                    fill='none'
                                    stroke='rgba(255,255,255,0.1)'
                                    strokeWidth='2.5'
                                />
                                <motion.circle
                                    cx='30'
                                    cy='30'
                                    r={R}
                                    fill='none'
                                    stroke='url(#scrollGrad)'
                                    strokeWidth='2.5'
                                    strokeLinecap='round'
                                    strokeDasharray={C}
                                    style={{
                                        pathLength: progress,
                                        filter:
                                            "drop-shadow(0 0 6px rgba(127,0,255,0.7))",
                                    }}
                                />
                                <defs>
                                    <linearGradient
                                        id='scrollGrad'
                                        x1='0%'
                                        y1='0%'
                                        x2='100%'
                                        y2='100%'
                                    >
                                        <stop offset='0%' stopColor='#7F00FF' />
                                        <stop offset='50%' stopColor='#FF8AFF' />
                                        <stop offset='100%' stopColor='#FCFA02' />
                                    </linearGradient>
                                </defs>
                            </svg>

                            {/* Arrow with launch animation on hover */}
                            <motion.div
                                variants={{
                                    rest: { y: 0, scale: 1, rotate: 0 },
                                    hover: {
                                        y: [-1, -6, -1],
                                        scale: [1, 1.25, 1.15],
                                        rotate: [0, 8, -8, 0],
                                    },
                                }}
                                transition={
                                    hovered
                                        ? {
                                              duration: 0.9,
                                              repeat: Infinity,
                                              ease: "easeInOut",
                                          }
                                        : { type: "spring", stiffness: 300, damping: 18 }
                                }
                                className='relative z-10'
                            >
                                <motion.div
                                    variants={{
                                        rest: { color: "#FFFFFF" },
                                        hover: { color: "#FCFA02" },
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <HiArrowUp className='text-xl md:text-2xl drop-shadow-lg' />
                                </motion.div>
                            </motion.div>

                            {/* Trail dots that rise on hover */}
                            <AnimatePresence>
                                {hovered &&
                                    [0, 1, 2].map((i) => (
                                        <motion.span
                                            key={`trail-${i}`}
                                            initial={{ y: 8, opacity: 0, scale: 0.4 }}
                                            animate={{
                                                y: -28,
                                                opacity: [0, 1, 0],
                                                scale: [0.4, 1, 0.4],
                                            }}
                                            exit={{ opacity: 0 }}
                                            transition={{
                                                duration: 1,
                                                repeat: Infinity,
                                                delay: i * 0.25,
                                                ease: "easeOut",
                                            }}
                                            className='absolute bottom-2 left-1/2 w-1 h-1 rounded-full pointer-events-none'
                                            style={{
                                                background: "#FCFA02",
                                                boxShadow: "0 0 6px #FCFA02",
                                                translateX: "-50%",
                                            }}
                                        />
                                    ))}
                            </AnimatePresence>

                            {/* Inner ring + glow on hover */}
                            <motion.div
                                aria-hidden
                                variants={{
                                    rest: { opacity: 0 },
                                    hover: { opacity: 1 },
                                }}
                                transition={{ duration: 0.3 }}
                                className='absolute inset-0 rounded-full pointer-events-none'
                                style={{
                                    boxShadow:
                                        "inset 0 0 0 1.5px rgba(255,255,255,0.4), inset 0 0 22px rgba(127,0,255,0.45)",
                                }}
                            />
                        </motion.button>

                        {/* Mobile mini-progress label */}
                        <div
                            className='md:hidden absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold tabular-nums text-white pointer-events-none'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.9), rgba(255,138,255,0.9))",
                                boxShadow: "0 4px 12px -2px rgba(127,0,255,0.6)",
                            }}
                        >
                            {pct}%
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(content, document.body);
};

export default FixedButton;
